import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SearchIcon from "../../../assets/images/icons/search.svg";
import YoutubeIcon from "../../../assets/images/admin/Youtube.png";
import * as actions from "../../../redux/actions/growthEngine";
import { formatNumber, getYoutubeVideoId } from "../../../helper/utility";
import { isValidDestinationUrl, normalizeDestinationUrl } from "./DestinationUrlStep";
import MusicLoader from "../../../helper/MusicLoader";
import { ObjectiveIcon } from "./ObjectiveIcon";

const MODES = [
    { id: "myChannel", label: "My channel" },
    { id: "contentUrl", label: "Content URL" },
];

export const buildYoutubeChannelUrl = (channel) => {
    if (!channel) return "";
    if (channel.handle) {
        const handle = channel.handle.startsWith("@") ? channel.handle : `@${channel.handle}`;
        return `https://www.youtube.com/${handle}`;
    }
    if (channel.customUrl) {
        const custom = String(channel.customUrl).replace(/^\//, "");
        return `https://www.youtube.com/${custom}`;
    }
    if (channel.channelId) {
        return `https://www.youtube.com/channel/${channel.channelId}`;
    }
    return "";
};

export const isYoutubeHostUrl = (value) => {
    try {
        const url = new URL(normalizeDestinationUrl(value));
        const host = url.hostname.replace(/^www\./i, "").toLowerCase();
        return host === "youtu.be" || host === "youtube.com" || host.endsWith(".youtube.com");
    } catch {
        return false;
    }
};

export const isYoutubeChannelUrl = (value) => {
    if (!isYoutubeHostUrl(value)) return false;
    try {
        const url = new URL(normalizeDestinationUrl(value));
        return /\/(@|channel\/|c\/|user\/)/i.test(url.pathname);
    } catch {
        return false;
    }
};

export const classifyYoutubeQuery = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "empty";
    if (isYoutubeChannelUrl(trimmed)) return "channel";
    if (getYoutubeVideoId(normalizeDestinationUrl(trimmed))) return "video";
    if (isYoutubeHostUrl(trimmed)) return "youtube";
    return "search";
};

export const isYoutubeDestinationSelected = (formData) => {
    const selected = formData?.selectedYoutubeContent;
    const url = normalizeDestinationUrl(formData?.destinationUrl);
    return Boolean(
        selected?.youtubeId &&
        selected?.contentType &&
        selected?.youtubeUrl &&
        isValidDestinationUrl(url)
    );
};

const toastWarn = (message) => {
    toast.warning(message, {
        position: "top-center",
        theme: "colored",
        hideProgressBar: true,
    });
};

const mergeUniqueVideos = (prev, next) => {
    const seen = new Set(prev.map((item) => item.videoId));
    return [...prev, ...next.filter((item) => item.videoId && !seen.has(item.videoId))];
};

export const YoutubeDestinationStep = () => {
    const dispatch = useDispatch();
    const { formData } = useSelector((state) => state.growthEngine);
    const selected = formData.selectedYoutubeContent || {};
    const contextChannel = formData.youtubeContextChannel || {};
    const rawMode = formData.youtubeDestinationMode || "myChannel";
    // Migrate away from removed "search" mode
    const mode = rawMode === "search" ? "contentUrl" : rawMode;

    const [channelSearchQuery, setChannelSearchQuery] = useState("");
    const [videoSearchQuery, setVideoSearchQuery] = useState("");
    const [channelSearchResults, setChannelSearchResults] = useState([]);
    const [videoSearchVideos, setVideoSearchVideos] = useState([]);
    const [videoSearchShorts, setVideoSearchShorts] = useState([]);
    const [videoSearchPageToken, setVideoSearchPageToken] = useState(null);
    const [videoSearchHasMore, setVideoSearchHasMore] = useState(false);
    const [lastVideoSearchQuery, setLastVideoSearchQuery] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingMoreVideoSearch, setLoadingMoreVideoSearch] = useState(false);
    const [channelVideos, setChannelVideos] = useState(
        formData.channelVideoShortsList?.videos || formData.youtubeVideoList || []
    );
    const [channelShorts, setChannelShorts] = useState(
        formData.channelVideoShortsList?.shorts || []
    );
    const [videosPageToken, setVideosPageToken] = useState(null);
    const [shortsPageToken, setShortsPageToken] = useState(null);
    const [videosHasMore, setVideosHasMore] = useState(false);
    const [shortsHasMore, setShortsHasMore] = useState(false);
    const [contentTab, setContentTab] = useState("videos"); // videos | shorts (my channel)
    const [videoResultsTab, setVideoResultsTab] = useState("videos"); // videos | shorts (content URL)

    const searchQuery = mode === "myChannel" ? channelSearchQuery : videoSearchQuery;
    const setSearchQuery = mode === "myChannel" ? setChannelSearchQuery : setVideoSearchQuery;

    const ignoreLoading = () => {};

    const applySelection = (content) => {
        dispatch(actions.updateFormData({
            selectedYoutubeContent: content,
            destinationUrl: content.youtubeUrl,
            selectedTargetTitle: content.title || "",
            selectedTargetImage: content.thumbnail || "",
        }));
    };

    const clearSelection = () => {
        dispatch(actions.updateFormData({
            selectedYoutubeContent: {},
            destinationUrl: "",
            selectedTargetTitle: "",
            selectedTargetImage: "",
        }));
    };

    // Keep search + channel context when switching tabs
    const setMode = (nextMode) => {
        dispatch(actions.updateFormData({ youtubeDestinationMode: nextMode }));
    };

    const loadChannelContent = async (channel) => {
        if (!channel?.channelId) return;
        setContentLoading(true);
        try {
            const result = await dispatch(
                actions.getYoutubeVideosList(channel.channelId, toast, ignoreLoading, {
                    maxResults: 20,
                })
            );
            setChannelVideos(result?.videos || []);
            setChannelShorts(result?.shorts || []);
            setVideosPageToken(result?.pagination?.videos?.nextPageToken || null);
            setShortsPageToken(result?.pagination?.shorts?.nextPageToken || null);
            setVideosHasMore(Boolean(result?.pagination?.videos?.hasMore));
            setShortsHasMore(Boolean(result?.pagination?.shorts?.hasMore));
            setContentTab("videos");
        } finally {
            setContentLoading(false);
        }
    };

    const loadMoreChannelContent = useCallback(async () => {
        if (!contextChannel?.channelId || loadingMore || contentLoading) return;

        const isShortsTab = contentTab === "shorts";
        const pageToken = isShortsTab ? shortsPageToken : videosPageToken;
        const hasMore = isShortsTab ? shortsHasMore : videosHasMore;
        if (!hasMore || !pageToken) return;

        setLoadingMore(true);
        try {
            const result = await dispatch(
                actions.getYoutubeVideosList(contextChannel.channelId, toast, ignoreLoading, {
                    type: isShortsTab ? "shorts" : "videos",
                    pageToken,
                    maxResults: 20,
                })
            );
            if (isShortsTab) {
                setChannelShorts((prev) => [...prev, ...(result?.shorts || [])]);
                setShortsPageToken(result?.pagination?.shorts?.nextPageToken || null);
                setShortsHasMore(Boolean(result?.pagination?.shorts?.hasMore));
            } else {
                setChannelVideos((prev) => [...prev, ...(result?.videos || [])]);
                setVideosPageToken(result?.pagination?.videos?.nextPageToken || null);
                setVideosHasMore(Boolean(result?.pagination?.videos?.hasMore));
            }
        } finally {
            setLoadingMore(false);
        }
    }, [
        contentLoading,
        contentTab,
        contextChannel?.channelId,
        dispatch,
        loadingMore,
        shortsHasMore,
        shortsPageToken,
        videosHasMore,
        videosPageToken,
    ]);

    const handleContentScroll = useCallback((event) => {
        const element = event.currentTarget;
        const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
        if (nearBottom) {
            loadMoreChannelContent();
        }
    }, [loadMoreChannelContent]);

    const loadMoreVideoSearch = useCallback(async () => {
        if (!lastVideoSearchQuery || !videoSearchHasMore || !videoSearchPageToken) return;
        if (loadingMoreVideoSearch || searchLoading) return;

        setLoadingMoreVideoSearch(true);
        try {
            const result = await dispatch(
                actions.searchYoutubeVideoAndShorts(lastVideoSearchQuery, toast, ignoreLoading, {
                    pageToken: videoSearchPageToken,
                    maxResults: 20,
                })
            );
            setVideoSearchVideos((prev) => mergeUniqueVideos(prev, result?.videos || []));
            setVideoSearchShorts((prev) => mergeUniqueVideos(prev, result?.shorts || []));
            setVideoSearchPageToken(result?.pagination?.nextPageToken || null);
            setVideoSearchHasMore(Boolean(result?.pagination?.hasMore));
        } finally {
            setLoadingMoreVideoSearch(false);
        }
    }, [
        dispatch,
        lastVideoSearchQuery,
        loadingMoreVideoSearch,
        searchLoading,
        videoSearchHasMore,
        videoSearchPageToken,
    ]);

    const handleVideoResultsScroll = useCallback((event) => {
        if (mode !== "contentUrl") return;
        const element = event.currentTarget;
        const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
        if (nearBottom) {
            loadMoreVideoSearch();
        }
    }, [loadMoreVideoSearch, mode]);

    const setContextChannel = async (channel) => {
        const context = {
            channelId: channel.channelId,
            title: channel.title,
            thumbnail: channel.thumbnail,
            handle: channel.handle || "",
            customUrl: channel.customUrl || "",
            youtubeUrl: buildYoutubeChannelUrl(channel),
            subscriberCount: channel.subscriberCount || null,
        };
        dispatch(actions.updateFormData({ youtubeContextChannel: context }));
        setChannelSearchResults([]);
        await loadChannelContent(context);
    };

    const selectChannelAsDestination = (channel = contextChannel) => {
        if (!channel?.channelId) return;
        applySelection({
            contentType: "channel",
            youtubeId: channel.channelId,
            youtubeUrl: channel.youtubeUrl || buildYoutubeChannelUrl(channel),
            title: channel.title,
            thumbnail: channel.thumbnail,
            channelName: channel.title,
            channelId: channel.channelId,
            handle: channel.handle || "",
            sourceMode: "myChannel",
            ownerChannelId: channel.channelId,
        });
    };

    const selectVideoAsDestination = (video, sourceMode = mode) => {
        applySelection({
            contentType: "video",
            youtubeId: video.videoId,
            youtubeUrl: video.videoUrl,
            title: video.title,
            thumbnail: video.thumbnail,
            channelName: video.channelName || contextChannel.title || "",
            channelId: video.channelId || contextChannel.channelId || "",
            handle: "",
            sourceMode,
            ownerChannelId: contextChannel.channelId || video.channelId || "",
        });
    };

    useEffect(() => {
        if (rawMode === "search") {
            dispatch(actions.updateFormData({ youtubeDestinationMode: "contentUrl" }));
        }
    }, [rawMode, dispatch]);

    useEffect(() => {
        if (!contextChannel?.channelId || mode !== "myChannel") return;
        loadChannelContent(contextChannel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextChannel?.channelId, mode]);

    const handleSearch = async () => {
        const query = searchQuery.trim();
        if (!query) return;
        const kind = classifyYoutubeQuery(query);

        setSearchLoading(true);
        try {
            if (mode === "myChannel") {
                if (kind === "video") {
                    toastWarn("That looks like a video URL. Switch to Content URL to use it.");
                    return;
                }
                const results = await dispatch(actions.searchYoutubeChannel(query, toast, ignoreLoading));
                const channels = Array.isArray(results) ? results : [];
                setChannelSearchResults(channels);
                setChannelSearchQuery("");
                if (kind === "channel" && channels.length === 1) {
                    await setContextChannel(channels[0]);
                }
            } else {
                // Content URL: paste video URL or search any video by title
                if (kind === "channel") {
                    toastWarn("That looks like a channel URL. Switch to My channel to use it.");
                    return;
                }
                const results = await dispatch(
                    actions.searchYoutubeVideoAndShorts(query, toast, ignoreLoading, {
                        maxResults: 20,
                    })
                );
                const videos = results?.videos || [];
                const shorts = results?.shorts || [];
                setVideoSearchVideos(videos);
                setVideoSearchShorts(shorts);
                setVideoSearchPageToken(results?.pagination?.nextPageToken || null);
                setVideoSearchHasMore(Boolean(results?.pagination?.hasMore));
                setLastVideoSearchQuery(kind === "video" ? "" : query);
                setVideoResultsTab(shorts.length && !videos.length ? "shorts" : "videos");
                setVideoSearchQuery("");
                if (kind === "video") {
                    const single =
                        videos.length === 1 && shorts.length === 0
                            ? videos[0]
                            : shorts.length === 1 && videos.length === 0
                                ? shorts[0]
                                : null;
                    if (single) {
                        selectVideoAsDestination(single, "contentUrl");
                    }
                }
            }
        } finally {
            setSearchLoading(false);
        }
    };

    const clearContextChannel = () => {
        dispatch(actions.updateFormData({ youtubeContextChannel: {} }));
        setChannelVideos([]);
        setChannelShorts([]);
        setVideosPageToken(null);
        setShortsPageToken(null);
        setVideosHasMore(false);
        setShortsHasMore(false);
        setContentTab("videos");
        setChannelSearchResults([]);
        if (selected.ownerChannelId && selected.ownerChannelId === contextChannel.channelId) {
            clearSelection();
        }
    };

    const placeholder =
        mode === "myChannel"
            ? "Paste your channel URL or search by name / @handle"
            : "Paste a video/Shorts URL or search by video title";

    const showChannelResults = mode === "myChannel" && channelSearchResults.length > 0;
    const showVideoResults =
        mode === "contentUrl" && (videoSearchVideos.length > 0 || videoSearchShorts.length > 0);
    const showContextPanel =
        mode === "myChannel" && Boolean(contextChannel?.channelId) && !showChannelResults;

    return (
        <div className="font-inter mb-8 xl:mb-[61px]" data-tour="youtube-destination-step">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <span className="inline-flex items-center gap-1.5 bg-theme_clr2 text-white text-[14px] font-medium rounded-[4px] py-2 px-4 mb-3">
                    <ObjectiveIcon icon="bi bi-youtube" name="YouTube Growth" variant="white" className="w-4 h-4" />
                    YouTube Growth
                </span>
                <h5 className="text-[22.5px] mb-1">Choose your YouTube destination</h5>
                <div className="description text-white text-opacity-80 text-[15px]">
                    Use your channel and pick content, or paste/search any video URL.
                </div>
            </div>

            <div className="info-box bg-white rounded-xl overflow-hidden">
                <div className="flex border-b border-slate-200">
                    {MODES.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => setMode(item.id)}
                            className={`flex-1 font-semibold px-4 py-3 ${
                                mode === item.id
                                    ? "border-b-2 border-theme_clr2 text-theme_clr2"
                                    : "text-gray-500 hover:text-theme_clr1"
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className="p-4 lg:p-5">
                    <div className="text-sm text-slate-600 mb-3">
                        {mode === "myChannel"
                            ? "Find your channel, then promote the channel or a video from it."
                            : "Paste a YouTube video/Shorts URL or search by title — no channel required."}
                    </div>

                    <div className="search-box relative mb-4">
                        <span className="icon absolute start-[16px] top-1/2 transform -translate-y-1/2 z-1">
                            <img src={SearchIcon} alt="Search Icon" className="block opacity-60" />
                        </span>
                        <input
                            type="search"
                            className="h-[45px] w-full bg-white border border-slate-300 text-base text-black placeholder:text-gray-400 focus:outline-none rounded-md ps-[45px] pe-[90px] py-2"
                            placeholder={
                                showContextPanel
                                    ? "Search a different channel…"
                                    : placeholder
                            }
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            disabled={searchLoading}
                        />
                        {searchLoading ? (
                            <span className="absolute top-1/2 -translate-y-1/2 end-4">
                                <MusicLoader />
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="text-theme_clr4 absolute top-1/2 -translate-y-1/2 end-4 font-medium"
                                onClick={handleSearch}
                            >
                                Search
                            </button>
                        )}
                    </div>

                    {selected?.youtubeId ? (
                        <div className="mb-4 rounded-lg border-2 border-green-500 bg-green-50 p-3">
                            <div className="text-xs font-semibold text-green-700 mb-2 uppercase tracking-wide">
                                Ad destination · {selected.contentType === "video" ? "video" : "channel"}
                            </div>
                            <div className="flex items-center gap-3">
                                <img
                                    src={selected.thumbnail || YoutubeIcon}
                                    alt={selected.title}
                                    className={selected.contentType === "channel"
                                        ? "w-12 h-12 rounded-full object-cover"
                                        : "w-[80px] h-[60px] rounded-md object-cover"}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[16px] font-bold text-theme_clr1 truncate">{selected.title}</div>
                                    {selected.contentType === "video" && selected.channelName ? (
                                        <div className="text-sm text-slate-600 truncate">
                                            Channel: {selected.channelName}
                                        </div>
                                    ) : null}
                                    <a
                                        href={selected.youtubeUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-theme_clr2 underline break-all"
                                    >
                                        {selected.youtubeUrl}
                                    </a>
                                </div>
                                <button
                                    type="button"
                                    className="text-xs text-red-500 font-medium"
                                    onClick={clearSelection}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {showContextPanel ? (
                        <div className="mb-4">
                            <div className="rounded-xl border-2 border-theme_clr1 p-3 mb-3">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={contextChannel.thumbnail || YoutubeIcon}
                                        alt={contextChannel.title}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">
                                            Selected channel
                                        </div>
                                        <div className="font-semibold text-theme_clr1 truncate">
                                            {contextChannel.title}
                                        </div>
                                        <div className="text-xs text-gray-500">{contextChannel.handle || ""}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-xs text-red-500 font-medium"
                                        onClick={clearContextChannel}
                                    >
                                        Change
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => selectChannelAsDestination(contextChannel)}
                                    className={`mt-3 w-full text-sm font-medium rounded-lg py-2.5 border-2 transition ${
                                        selected.contentType === "channel" &&
                                        selected.youtubeId === contextChannel.channelId
                                            ? "bg-green-100 border-green-500 text-green-800"
                                            : "bg-theme_clr1 text-white border-theme_clr1 hover:opacity-90"
                                    }`}
                                >
                                    {selected.contentType === "channel" &&
                                    selected.youtubeId === contextChannel.channelId
                                        ? "Using channel as destination ✓"
                                        : "Use this channel as destination"}
                                </button>
                            </div>

                            <div className="text-sm font-semibold text-theme_clr1 mb-2">
                                Or pick content from this channel
                            </div>
                            <div className="flex border-b border-slate-200 mb-2">
                                {[
                                    { id: "videos", label: `Videos (${channelVideos.length})` },
                                    { id: "shorts", label: `Shorts (${channelShorts.length})` },
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setContentTab(tab.id)}
                                        className={`px-4 py-2 text-sm font-semibold ${
                                            contentTab === tab.id
                                                ? "border-b-2 border-theme_clr2 text-theme_clr2"
                                                : "text-gray-500 hover:text-theme_clr1"
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div
                                className="max-h-[360px] divide-y divide-gray-200 overflow-auto border border-slate-200 rounded-lg"
                                onScroll={handleContentScroll}
                            >
                                {contentLoading ? (
                                    <div className="py-6"><MusicLoader /></div>
                                ) : (() => {
                                    const list = contentTab === "shorts" ? channelShorts : channelVideos;
                                    const emptyLabel = contentTab === "shorts"
                                        ? "No Shorts found for this channel."
                                        : "No videos found for this channel.";
                                    if (!list.length) {
                                        return (
                                            <div className="text-center text-gray-500 py-6 text-sm">
                                                {emptyLabel}
                                            </div>
                                        );
                                    }
                                    return (
                                        <>
                                            {list.map((video) => {
                                                const isSelected =
                                                    selected.contentType === "video" &&
                                                    selected.youtubeId === video.videoId;
                                                const isShort = video.type === "short" || contentTab === "shorts";
                                                return (
                                                    <button
                                                        type="button"
                                                        key={`${contentTab}-${video.videoId}`}
                                                        onClick={() => {
                                                            if (isSelected) clearSelection();
                                                            else selectVideoAsDestination({
                                                                ...video,
                                                                videoUrl: isShort
                                                                    ? (video.videoUrl || `https://www.youtube.com/shorts/${video.videoId}`)
                                                                    : video.videoUrl,
                                                            }, "myChannel");
                                                        }}
                                                        className={`w-full text-left flex items-start gap-3 p-3 transition-colors ${
                                                            isSelected ? "bg-green-100" : "hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        <div className="relative">
                                                            <img
                                                                src={video.thumbnail || YoutubeIcon}
                                                                alt={video.title}
                                                                className={isShort
                                                                    ? "w-[54px] h-[80px] rounded-md object-cover"
                                                                    : "w-[80px] h-[60px] rounded-md object-cover"}
                                                            />
                                                            {isShort ? (
                                                                <span className="absolute bottom-1 left-1 bg-black/75 text-white text-[10px] font-semibold rounded px-1">
                                                                    Shorts
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium text-sm text-theme_clr1">
                                                                {video.title}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {video.publishedAt
                                                                    ? new Date(video.publishedAt).toLocaleDateString()
                                                                    : ""}
                                                            </div>
                                                        </div>
                                                        {isSelected ? (
                                                            <i className="bi bi-check-circle-fill text-green-600 text-lg mt-2"></i>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                            {loadingMore ? (
                                                <div className="py-4"><MusicLoader /></div>
                                            ) : null}
                                            {!(contentTab === "shorts" ? shortsHasMore : videosHasMore) && list.length > 0 ? (
                                                <div className="text-center text-gray-400 py-3 text-xs">
                                                    No more {contentTab === "shorts" ? "Shorts" : "videos"}
                                                </div>
                                            ) : null}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    ) : null}

                    <div
                        className="max-h-[400px] divide-y divide-gray-200 overflow-auto"
                        onScroll={showVideoResults ? handleVideoResultsScroll : undefined}
                    >
                        {searchLoading ? (
                            <div className="py-6"><MusicLoader /></div>
                        ) : showChannelResults ? (
                            channelSearchResults.map((channel) => (
                                <button
                                    type="button"
                                    key={channel.channelId}
                                    onClick={() => setContextChannel(channel)}
                                    className="w-full text-left flex items-center gap-3 p-3 transition-colors hover:bg-slate-50"
                                >
                                    <img
                                        src={channel.thumbnail || YoutubeIcon}
                                        alt={channel.title}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm text-theme_clr1 truncate">
                                            {channel.title}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {channel.handle || channel.customUrl || ""}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {channel.subscriberCount
                                                ? `${formatNumber(channel.subscriberCount)} subscribers`
                                                : "Subscriber count hidden"}
                                        </div>
                                    </div>
                                    <span className="text-xs text-theme_clr2 font-medium whitespace-nowrap">
                                        Select
                                    </span>
                                </button>
                            ))
                        ) : showVideoResults ? (
                            <>
                                <div className="flex border-b border-slate-200 mb-2">
                                    {[
                                        { id: "videos", label: `Videos (${videoSearchVideos.length})` },
                                        { id: "shorts", label: `Shorts (${videoSearchShorts.length})` },
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setVideoResultsTab(tab.id)}
                                            className={`px-4 py-2 text-sm font-semibold ${
                                                videoResultsTab === tab.id
                                                    ? "border-b-2 border-theme_clr2 text-theme_clr2"
                                                    : "text-gray-500 hover:text-theme_clr1"
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                                {(() => {
                                    const list = videoResultsTab === "shorts"
                                        ? videoSearchShorts
                                        : videoSearchVideos;
                                    const emptyLabel = videoResultsTab === "shorts"
                                        ? "No Shorts found."
                                        : "No videos found.";
                                    if (!list.length) {
                                        return (
                                            <div className="text-center text-gray-500 py-6 text-sm">
                                                {emptyLabel}
                                            </div>
                                        );
                                    }
                                    return (
                                        <>
                                            {list.map((video) => {
                                                const isSelected =
                                                    selected.contentType === "video" &&
                                                    selected.youtubeId === video.videoId;
                                                const isShort = video.type === "short" || videoResultsTab === "shorts";
                                                return (
                                                    <button
                                                        type="button"
                                                        key={`${videoResultsTab}-${video.videoId}`}
                                                        onClick={() => {
                                                            if (isSelected) clearSelection();
                                                            else selectVideoAsDestination({
                                                                ...video,
                                                                videoUrl: isShort
                                                                    ? (video.videoUrl || `https://www.youtube.com/shorts/${video.videoId}`)
                                                                    : video.videoUrl,
                                                            }, "contentUrl");
                                                        }}
                                                        className={`w-full text-left flex items-start gap-3 p-3 transition-colors ${
                                                            isSelected ? "bg-green-100" : "hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        <div className="relative">
                                                            <img
                                                                src={video.thumbnail || YoutubeIcon}
                                                                alt={video.title}
                                                                className={isShort
                                                                    ? "w-[54px] h-[80px] rounded-md object-cover"
                                                                    : "w-[80px] h-[60px] rounded-md object-cover"}
                                                            />
                                                            {isShort ? (
                                                                <span className="absolute bottom-1 left-1 bg-black/75 text-white text-[10px] font-semibold rounded px-1">
                                                                    Shorts
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium text-sm text-theme_clr1">
                                                                {video.title}
                                                            </div>
                                                            <div className="text-sm flex items-center gap-1 text-slate-600">
                                                                <img src={YoutubeIcon} alt="YouTube" className="w-[18px]" />
                                                                {video.channelName}
                                                            </div>
                                                        </div>
                                                        {isSelected ? (
                                                            <i className="bi bi-check-circle-fill text-green-600 text-lg mt-2"></i>
                                                        ) : null}
                                                    </button>
                                                );
                                            })}
                                            {loadingMoreVideoSearch ? (
                                                <div className="py-4"><MusicLoader /></div>
                                            ) : null}
                                            {!videoSearchHasMore && list.length > 0 && lastVideoSearchQuery ? (
                                                <div className="text-center text-gray-400 py-3 text-xs">
                                                    No more results
                                                </div>
                                            ) : null}
                                        </>
                                    );
                                })()}
                            </>
                        ) : !showContextPanel ? (
                            <div className="text-center text-gray-500 py-6 text-sm">
                                {mode === "myChannel"
                                    ? "Search for your YouTube channel to continue."
                                    : "Paste a video URL or search by title to continue."}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};
